import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brain, RefreshCw, AlertTriangle, Sparkles, Moon, Heart, Target, CheckCircle2, Calendar } from "lucide-react";
import { formatUKDate } from "@/lib/dateUtils";
import { toast } from "sonner";
import { useState } from "react";

interface SessionPrepBriefProps {
  clientId: string;
  clinicianId: string;
}

export function SessionPrepBrief({ clientId, clinicianId }: SessionPrepBriefProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch upcoming session for this client
  const { data: upcomingSession } = useQuery({
    queryKey: ['upcoming-session', clientId, clinicianId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('therapy_sessions')
        .select('*')
        .eq('client_id', clientId)
        .eq('clinician_id', clinicianId)
        .gte('session_date', new Date().toISOString().split('T')[0])
        .order('session_date', { ascending: true })
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch existing brief for this session
  const { data: brief, refetch } = useQuery({
    queryKey: ['session-prep-brief', upcomingSession?.id],
    enabled: !!upcomingSession?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('session_preparation_briefs')
        .select('*')
        .eq('session_id', upcomingSession!.id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch client data for brief generation
  const { data: clientData } = useQuery({
    queryKey: ['client-session-data', clientId],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateStr = thirtyDaysAgo.toISOString().split('T')[0];

      const [checkins, goals, journal, crisis] = await Promise.all([
        supabase
          .from('daily_checkins')
          .select('*')
          .eq('user_id', clientId)
          .gte('date', dateStr)
          .order('date', { ascending: false })
          .limit(14),
        supabase
          .from('goals')
          .select('*')
          .eq('user_id', clientId)
          .order('created_at', { ascending: false }),
        supabase
          .from('journal_entries')
          .select('*')
          .eq('user_id', clientId)
          .gte('created_at', thirtyDaysAgo.toISOString())
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('adverse_events')
          .select('*')
          .eq('user_id', clientId)
          .gte('created_at', thirtyDaysAgo.toISOString())
      ]);

      return {
        checkins: checkins.data || [],
        goals: goals.data || [],
        journal: journal.data || [],
        crisis: crisis.data || []
      };
    }
  });

  // Generate brief
  const generateBrief = async () => {
    if (!clientData) {
      toast.error("Unable to fetch client data");
      return;
    }

    if (!upcomingSession?.id) {
      toast.error("No upcoming session found for this client");
      return;
    }

    setIsGenerating(true);

    try {
      // Analyse mood trends
      const moodMap: Record<string, number> = {
        'amazing': 5, 'good': 4, 'okay': 3, 'struggling': 2, 'crisis': 1
      };
      
      const moodScores = clientData.checkins
        .filter((c: any) => c.daily_feeling)
        .map((c: any) => ({
          date: c.date,
          score: moodMap[c.daily_feeling] || 3
        }));

      const avgMood = moodScores.length > 0 
        ? moodScores.reduce((sum: number, m: any) => sum + m.score, 0) / moodScores.length 
        : null;

      const recentMood = moodScores.slice(0, 7);
      const priorMood = moodScores.slice(7, 14);
      const recentAvg = recentMood.length > 0 
        ? recentMood.reduce((sum: number, m: any) => sum + m.score, 0) / recentMood.length 
        : null;
      const priorAvg = priorMood.length > 0 
        ? priorMood.reduce((sum: number, m: any) => sum + m.score, 0) / priorMood.length 
        : null;

      const moodTrend = recentAvg && priorAvg 
        ? recentAvg > priorAvg ? 'improving' : recentAvg < priorAvg ? 'declining' : 'stable'
        : 'insufficient data';

      // Sleep analysis
      const sleepData = clientData.checkins
        .filter((c: any) => c.hours_slept)
        .map((c: any) => ({
          date: c.date,
          hours: c.hours_slept
        }));

      const avgSleep = sleepData.length > 0
        ? sleepData.reduce((sum: number, s: any) => sum + s.hours, 0) / sleepData.length
        : null;

      // Goal progress
      const completedGoals = clientData.goals.filter((g: any) => g.completed).length;
      const totalGoals = clientData.goals.length;

      // Extract journal themes (simple keyword analysis)
      const journalText = clientData.journal.map((j: any) => j.content || '').join(' ').toLowerCase();
      const themes: string[] = [];
      if (journalText.includes('anxious') || journalText.includes('worry')) themes.push('Anxiety');
      if (journalText.includes('sad') || journalText.includes('depressed')) themes.push('Low Mood');
      if (journalText.includes('relationship') || journalText.includes('partner')) themes.push('Relationships');
      if (journalText.includes('work') || journalText.includes('job')) themes.push('Work Stress');
      if (journalText.includes('sleep') || journalText.includes('tired')) themes.push('Sleep Issues');

      // Key observations
      const observations: string[] = [];
      if (moodTrend === 'declining') observations.push('Mood has been declining over the past week');
      if (moodTrend === 'improving') observations.push('Positive mood improvement noted');
      if (avgSleep && avgSleep < 6) observations.push('Below optimal sleep average detected');
      if (clientData.crisis.length > 0) observations.push(`${clientData.crisis.length} crisis event(s) in the past 30 days`);
      if (completedGoals > 0) observations.push(`${completedGoals} goal(s) achieved recently`);

      // Suggested topics
      const suggestedTopics: string[] = [];
      if (themes.includes('Anxiety')) suggestedTopics.push('Explore anxiety triggers and coping strategies');
      if (themes.includes('Relationships')) suggestedTopics.push('Discuss relationship dynamics');
      if (avgSleep && avgSleep < 7) suggestedTopics.push('Review sleep hygiene practices');
      if (moodTrend === 'declining') suggestedTopics.push('Assess for mood deterioration factors');
      if (totalGoals === 0) suggestedTopics.push('Consider setting therapeutic goals');

      // Risk indicators
      const riskIndicators: any = {};
      if (moodTrend === 'declining' && avgMood && avgMood < 2.5) riskIndicators.moodRisk = 'elevated';
      if (clientData.crisis.length > 0) riskIndicators.recentCrisis = true;

      // Save brief
      const briefData = {
        session_id: upcomingSession.id,
        client_id: clientId,
        clinician_id: clinicianId,
        mood_summary: {
          average: avgMood?.toFixed(1),
          trend: moodTrend,
          recentScores: moodScores.slice(0, 7)
        },
        sleep_summary: {
          average: avgSleep?.toFixed(1),
          data: sleepData.slice(0, 7)
        },
        check_in_summary: {
          totalCheckins: clientData.checkins.length,
          lastCheckin: clientData.checkins[0]?.date
        },
        journal_themes: themes,
        crisis_events: clientData.crisis,
        goal_progress: {
          completed: completedGoals,
          total: totalGoals
        },
        key_observations: observations,
        suggested_topics: suggestedTopics,
        risk_indicators: riskIndicators,
        generated_at: new Date().toISOString()
      };

      // Upsert the brief
      const { error } = await supabase
        .from('session_preparation_briefs')
        .upsert(briefData, { onConflict: 'session_id' });

      if (error) throw error;

      await refetch();
      toast.success("Session brief generated");
    } catch (error) {
      console.error('Error generating brief:', error);
      toast.error("Failed to generate brief");
    } finally {
      setIsGenerating(false);
    }
  };

  const getMoodTrendColour = (trend: string) => {
    switch (trend) {
      case 'improving': return 'text-green-600 bg-green-500/20';
      case 'declining': return 'text-red-600 bg-red-500/20';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Session Preparation Brief
          </CardTitle>
          {upcomingSession && (
            <Button
              size="sm"
              variant="outline"
              onClick={generateBrief}
              disabled={isGenerating}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
              {brief ? 'Refresh' : 'Generate'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!upcomingSession ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No upcoming session</p>
            <p className="text-sm text-muted-foreground">
              Schedule a session to generate a preparation brief
            </p>
          </div>
        ) : !brief ? (
          <div className="text-center py-8">
            <Brain className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No preparation brief yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Generate an AI-powered summary for the upcoming session on {formatUKDate(new Date(upcomingSession.session_date))}
            </p>
            <Button onClick={generateBrief} disabled={isGenerating}>
              <Sparkles className="h-4 w-4 mr-1" />
              Generate Brief
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Generated: {brief.generated_at ? formatUKDate(new Date(brief.generated_at)) : 'Unknown'}
            </p>

            {/* Mood Summary */}
            {brief.mood_summary && typeof brief.mood_summary === 'object' && (
              <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="h-4 w-4 text-pink-500" />
                  <span className="text-sm font-medium">Mood Overview</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold">{(brief.mood_summary as any).average}/5</span>
                  <Badge className={getMoodTrendColour((brief.mood_summary as any).trend)}>
                    {(brief.mood_summary as any).trend}
                  </Badge>
                </div>
              </div>
            )}

            {/* Sleep Summary */}
            {brief.sleep_summary && typeof brief.sleep_summary === 'object' && (brief.sleep_summary as any).average && (
              <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                <div className="flex items-center gap-2 mb-2">
                  <Moon className="h-4 w-4 text-indigo-500" />
                  <span className="text-sm font-medium">Sleep Average</span>
                </div>
                <span className="text-2xl font-bold">{(brief.sleep_summary as any).average} hrs</span>
              </div>
            )}

            {/* Goal Progress */}
            {brief.goal_progress && typeof brief.goal_progress === 'object' && (
              <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">Goal Progress</span>
                </div>
                <span className="text-2xl font-bold">
                  {(brief.goal_progress as any).completed}/{(brief.goal_progress as any).total}
                </span>
                <span className="text-sm text-muted-foreground ml-2">completed</span>
              </div>
            )}

            {/* Risk Indicators */}
            {brief.risk_indicators && typeof brief.risk_indicators === 'object' && Object.keys(brief.risk_indicators).length > 0 && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-600">Risk Indicators</span>
                </div>
                <ul className="text-sm space-y-1">
                  {(brief.risk_indicators as any).moodRisk && (
                    <li>Elevated mood risk detected</li>
                  )}
                  {(brief.risk_indicators as any).recentCrisis && (
                    <li>Recent crisis event(s) on record</li>
                  )}
                </ul>
              </div>
            )}

            {/* Key Observations */}
            {brief.key_observations && Array.isArray(brief.key_observations) && brief.key_observations.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Key Observations
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {brief.key_observations.map((obs: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      {obs}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Suggested Topics */}
            {brief.suggested_topics && Array.isArray(brief.suggested_topics) && brief.suggested_topics.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent" />
                  Suggested Topics
                </h4>
                <div className="flex flex-wrap gap-2">
                  {brief.suggested_topics.map((topic: string, idx: number) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {topic}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Journal Themes */}
            {brief.journal_themes && Array.isArray(brief.journal_themes) && (brief.journal_themes as string[]).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Journal Themes</h4>
                <div className="flex flex-wrap gap-2">
                  {(brief.journal_themes as string[]).map((theme: string, idx: number) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {theme}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
